/**
 * Decodes training-log signature payloads from API (Buffer / { type, data }).
 * Same rules as Training Log list: raw PNG/JPEG/GIF, data URLs, SVG text, or base64 ASCII.
 */
export function decodeSignatureBuffer(bufferLike) {
  if (bufferLike == null) return null;
  if (typeof bufferLike === "string") {
    const t = bufferLike.trim();
    if (!t) return null;
    if (t.startsWith("data:image") || t.startsWith("data:application")) {
      return { kind: "img", src: t };
    }
    const cleaned = t.replace(/\s/g, "");
    if (cleaned.length >= 4 && /^[A-Za-z0-9+/]+=*$/.test(cleaned)) {
      return { kind: "img", src: `data:image/png;base64,${cleaned}` };
    }
    return { kind: "text", text: t };
  }
  if (!bufferLike?.data) return null;
  let bytes;
  if (bufferLike.data instanceof Uint8Array) {
    bytes = bufferLike.data;
  } else if (Array.isArray(bufferLike.data)) {
    bytes = new Uint8Array(bufferLike.data);
  } else {
    return null;
  }
  if (bytes.length === 0) return null;

  if (
    bytes.length >= 4 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    let bin = "";
    for (let i = 0; i < bytes.length; i += 1) {
      bin += String.fromCharCode(bytes[i]);
    }
    return { kind: "img", src: `data:image/png;base64,${btoa(bin)}` };
  }

  if (
    bytes.length >= 3 &&
    bytes[0] === 0xff &&
    bytes[1] === 0xd8 &&
    bytes[2] === 0xff
  ) {
    let bin = "";
    for (let i = 0; i < bytes.length; i += 1) {
      bin += String.fromCharCode(bytes[i]);
    }
    return { kind: "img", src: `data:image/jpeg;base64,${btoa(bin)}` };
  }

  if (
    bytes.length >= 6 &&
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46
  ) {
    let bin = "";
    for (let i = 0; i < bytes.length; i += 1) {
      bin += String.fromCharCode(bytes[i]);
    }
    return { kind: "img", src: `data:image/gif;base64,${btoa(bin)}` };
  }

  let text = "";
  try {
    text = new TextDecoder("utf-8", { fatal: false }).decode(bytes).trim();
  } catch {
    return null;
  }
  if (!text) return null;

  if (text.startsWith("data:image") || text.startsWith("data:application")) {
    return { kind: "img", src: text };
  }

  const trimmed = text.trim();
  if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) {
    return {
      kind: "img",
      src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(trimmed)}`,
    };
  }

  const cleaned = trimmed.replace(/\s/g, "");
  // Bare base64 (often stored as ASCII in Buffer — not raw image bytes).
  // Allow shorter chunks than the main Training Log table (40) so small signatures render.
  if (
    cleaned.length >= 4 &&
    /^[A-Za-z0-9+/]+=*$/.test(cleaned)
  ) {
    return { kind: "img", src: `data:image/png;base64,${cleaned}` };
  }

  return { kind: "text", text: trimmed };
}

/** For Document / simple tables: img src or fallback text line. */
export function getTrainingLogSignatureCell(sig) {
  const d = decodeSignatureBuffer(sig);
  if (!d) return { type: "empty" };
  if (d.kind === "img") return { type: "img", src: d.src };
  if (d.kind === "text" && d.text) {
    return { type: "text", text: d.text };
  }
  return { type: "empty" };
}
